import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import usePrevious from 'hooks/usePrevious'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'

const ColourfulLoadingAnimation = styled(LoadingAnimation)`
  margin-top: ${rem(16)};
  color: ${props => props.theme.palette.brand.primary.normal};
`

function toggleSelectedSet(selectedSet: Set<string>, value: string) {
  const nextSelectedSet = new Set([...selectedSet.values()])

  if (nextSelectedSet.has(value)) {
    nextSelectedSet.delete(value)
  } else {
    nextSelectedSet.add(value)
  }

  return nextSelectedSet
}

type Option = {
  label: string;
  value: string;
};

function generateOptionItems(options: Array<Option>, localStates: Set<string>) {
  return options.map((item) => ({
    label: item.label,
    value: item.value,
    checked: localStates.has(item.value),
  }))
}

export type ReviewListFilterSubmit = {
  ratings: Array<string>;
  companionship: Array<string>;
  months: Array<string>;
}

const availableFilters = {
  ratings: [
    { label: 'Exceptional: 9+', value: 'exceptional' },
    { label: 'Good: 7.0 - 8.9', value: 'good' },
    { label: 'Okay: 5.0 - 6.9', value: 'okay' },
    { label: 'Poor: 3.0 - 4.9', value: 'poor' },
    { label: 'Very poor: 1.0 - 2.9', value: 'very-poor' },
  ],
  companionship: [
    { label: 'Solo travellers', value: 'alone' },
    { label: 'Couples', value: 'partner' },
    { label: 'Families', value: 'family' },
    { label: 'Friends', value: 'friends' },
    { label: 'Business travellers', value: 'work' },
  ],
  months: [
    { label: 'Dec - Feb', value: '12,1,2' },
    { label: 'Mar - May', value: '3,4,5' },
    { label: 'Jun - Aug', value: '6,7,8' },
    { label: 'Sep - Nov', value: '9,10,11' },
  ],
}

interface Props {
  open?: boolean;
  onClose?: () => void;
  onApply?: (values: ReviewListFilterSubmit) => void;
  existingFilters?: any;
  isLoading?: boolean;
}

function UserReviewFilters(props: Props) {
  const {
    open,
    onClose,
    existingFilters,
    onApply,
    isLoading,
  } = props

  const [localRatings, setLocalRatings] = useState<Set<string>>(new Set())
  const [localCompanionship, setLocalCompanionship] = useState<Set<string>>(new Set())
  const [localMonths, setLocalMonths] = useState<Set<string>>(new Set())

  const previouslyOpen = usePrevious(open, false)

  useEffect(() => {
    if (!previouslyOpen && open) {
      // just opened, reset based on existing filters
      setLocalRatings(new Set(existingFilters.ratings))
      setLocalCompanionship(new Set(existingFilters.companionship))
      setLocalMonths(new Set(existingFilters.months))
    }
  }, [existingFilters, open, previouslyOpen])

  const onFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    onApply?.({
      ratings: [...localRatings],
      companionship: [...localCompanionship],
      months: [...localMonths],
    })

    onClose?.()
  }

  const onRatingsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextRatings = toggleSelectedSet(localRatings, e.target.value)
    setLocalRatings(nextRatings)
  }, [localRatings])

  const onCompanionshipChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextCompanionship = toggleSelectedSet(localCompanionship, e.target.value)
    setLocalCompanionship(nextCompanionship)
  }, [localCompanionship])

  const onMonthsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const nextMonths = toggleSelectedSet(localMonths, e.target.value)
    setLocalMonths(nextMonths)
  }, [localMonths])

  const ratingsItems = useMemo(
    () => generateOptionItems(availableFilters.ratings, localRatings),
    [localRatings],
  )

  const companionshipItems = useMemo(
    () => generateOptionItems(availableFilters.companionship, localCompanionship),
    [localCompanionship],
  )

  const monthsItems = useMemo(
    () => generateOptionItems(availableFilters.months, localMonths),
    [localMonths],
  )

  const onRatingsReset = useCallback(() => {
    setLocalRatings(new Set())
  }, [])

  const onCompanionshipReset = useCallback(() => {
    setLocalCompanionship(new Set())
  }, [])

  const onMonthsReset = useCallback(() => {
    setLocalMonths(new Set())
  }, [])

  const onFormReset = useCallback(() => {
    onRatingsReset()
    onCompanionshipReset()
    onMonthsReset()
  }, [onRatingsReset, onCompanionshipReset, onMonthsReset])

  return (
    <ModalBase mode="drawer" isOpen={open} onClose={onClose}>
      <form onSubmit={onFilterSubmit}>
        <ModalHeader title="Filters" onCloseButtonClick={onClose} />
        <ModalBody>
          <ModalContent>
            {isLoading && (
              <CSSBreakpoint min="tablet">
                <ColourfulLoadingAnimation size={8} />
              </CSSBreakpoint>
            )}
            <FilterPanelFilterGroup
              title="Ratings"
              onReset={onRatingsReset}
              canReset={localRatings.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="ratings"
                items={ratingsItems}
                onChange={onRatingsChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
            <FilterPanelFilterGroup
              title="Travellers"
              onReset={onCompanionshipReset}
              canReset={localCompanionship.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="companionship"
                items={companionshipItems}
                onChange={onCompanionshipChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
            <FilterPanelFilterGroup
              title="Months"
              onReset={onMonthsReset}
              canReset={localMonths.size > 0}
            >
              <FilterPanelCheckboxGroup
                name="months"
                items={monthsItems}
                onChange={onMonthsChange}
                maxItems={10}
              />
            </FilterPanelFilterGroup>
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: 'View reviews',
            type: 'submit',
            'data-testid': 'view-reviews-filter-button',
          }}
          secondaryActionProps={{
            children: 'Reset all',
            onClick: onFormReset,
          }}
        />
      </form>
    </ModalBase>
  )
}

export default UserReviewFilters
